import { useState } from "react";
import { motion } from "framer-motion";
import { PlayCircle, Clock, CaretDown } from "@phosphor-icons/react";
import VerdictBadge from "./VerdictBadge";
import ShareButton from "./ShareButton";
import PlatformBadge from "./PlatformBadge";

function formatDuration(s) {
  if (!s) return "";
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${String(sec).padStart(2, "0")}`;
}

export default function VideoSidebar({ metadata, videoUrl, overallVerdict, overallConfidence, overallSummary, cached, processingMs, transcriptExcerpt, cacheKey }) {
  const thumb = metadata?.thumbnail_url;
  const [showTranscript, setShowTranscript] = useState(false);

  return (
    <aside data-testid="video-sidebar" className="flex flex-col gap-5">
      {/* Video thumbnail */}
      <div className="border-2 border-black bg-white overflow-hidden">
        {thumb ? (
          <a href={videoUrl} target="_blank" rel="noopener noreferrer" className="block relative group">
            <img
              src={thumb}
              alt={metadata?.title || "video"}
              className="w-full aspect-video object-cover"
            />
            <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity">
              <PlayCircle weight="fill" className="w-14 h-14 text-white" />
            </div>
          </a>
        ) : (
          <div className="w-full aspect-video bg-zinc-100 flex items-center justify-center">
            <PlayCircle weight="duotone" className="w-14 h-14 text-zinc-400" />
          </div>
        )}
        <div className="p-4 border-t border-zinc-200">
          <div className="flex items-center gap-2 mb-1.5">
            <div className="font-mono-plex text-[10px] font-semibold text-zinc-500">
              {metadata?.channel ? metadata.channel : "VIDEO"}
            </div>
            {metadata?.platform && (
              <PlatformBadge platform={metadata.platform} />
            )}
          </div>
          <h2 className="font-display text-lg text-zinc-950 leading-tight mb-2">
            {metadata?.title || "Untitled video"}
          </h2>
          <div className="flex items-center gap-3 text-[11px] text-zinc-500 font-mono-plex">
            {metadata?.duration_seconds ? (
              <span className="flex items-center gap-1">
                <Clock weight="bold" className="w-3 h-3" />
                {formatDuration(metadata.duration_seconds)}
              </span>
            ) : null}
            {cached ? <span className="text-emerald-700">CACHED</span> : <span>FRESH</span>}
            {processingMs != null && <span>· {(processingMs / 1000).toFixed(1)}s</span>}
          </div>
        </div>
      </div>

      {/* Overall Verdict Card — Neo-brutalist sharp rectangle */}
      {overallVerdict && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          data-testid="overall-verdict-card"
          className="border-2 border-black bg-white p-5 shadow-[6px_6px_0px_rgba(0,0,0,1)]"
        >
          <div className="font-mono-plex text-[10px] font-semibold text-zinc-500 mb-3">
            OVERALL VERDICT
          </div>
          <div className="mb-3">
            <VerdictBadge verdict={overallVerdict} size="lg" shape="rectangle" confidence={overallConfidence} />
          </div>
          {overallSummary && (
            <p className="text-[13px] text-zinc-700 leading-relaxed mb-4">
              {overallSummary}
            </p>
          )}
          {cacheKey && (
            <div className="pt-3 border-t border-zinc-200">
              <div className="font-mono-plex text-[10px] font-semibold text-zinc-500 mb-2">
                SHARE THE RECEIPTS
              </div>
              <ShareButton cacheKey={cacheKey} verdict={overallVerdict} />
            </div>
          )}
        </motion.div>
      )}

      {/* Transcript excerpt — collapsed by default to declutter */}
      {transcriptExcerpt && (
        <div className="border border-zinc-300 bg-white">
          <button
            type="button"
            onClick={() => setShowTranscript((v) => !v)}
            data-testid="toggle-transcript-excerpt"
            className="w-full flex items-center justify-between px-4 py-2.5 group"
          >
            <span className="font-mono-plex text-[10px] font-semibold text-zinc-500">
              TRANSCRIPT · EXCERPT
            </span>
            <CaretDown
              weight="bold"
              className={`w-3.5 h-3.5 text-zinc-900 transition-transform ${showTranscript ? "rotate-180" : ""}`}
            />
          </button>
          {showTranscript && (
            <div className="px-4 pb-4 pt-1 border-t border-zinc-200">
              <p className="text-[12.5px] text-zinc-700 leading-relaxed italic line-clamp-6">
                &ldquo;{transcriptExcerpt}&rdquo;
              </p>
            </div>
          )}
        </div>
      )}
    </aside>
  );
}
